import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Plus, Truck, X, AlertTriangle, Package, ShoppingCart, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function OpsDispatches({ user }) {
  const { toast } = useToast();
  const [locations, setLocations] = useState({ kitchen: null, outlets: [] });
  const [kitchenInventory, setKitchenInventory] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [customItemName, setCustomItemName] = useState("");
  const [customItemUnit, setCustomItemUnit] = useState("pcs");
  
  const [form, setForm] = useState({
    outlet_id: "",
    items: [],
    vehicle_number: "",
    driver_name: "",
    num_persons: 1,
    transport_medium: "",
    notes: ""
  });

  useEffect(() => {
    if (user?.user_id) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      // Fetch locations for this ops manager
      const locRes = await fetch(`${API_URL}/api/ops-extended/my-locations/${user.user_id}`, { credentials: "include" });
      if (locRes.ok) {
        const locData = await locRes.json();
        setLocations(locData);
        
        if (locData.kitchen) {
          // Fetch kitchen inventory
          const invRes = await fetch(`${API_URL}/api/ops-extended/kitchen-inventory/${locData.kitchen.kitchen_id}`, { credentials: "include" });
          if (invRes.ok) setKitchenInventory(await invRes.json());
          
          // Fetch all dispatches for this kitchen (not just today)
          const dispRes = await fetch(`${API_URL}/api/ops-extended/dispatches?kitchen_id=${locData.kitchen.kitchen_id}`, { credentials: "include" });
          if (dispRes.ok) {
            const dispData = await dispRes.json();
            setDispatches(dispData);
          }
        }
      }
      
      // Fetch pending orders
      const ordersRes = await fetch(`${API_URL}/api/ops/orders`, { credentials: "include" });
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        const pending = ordersData.filter(o => 
          o.status === "confirmed" || o.status === "processing"
        );
        setPendingOrders(pending);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = (item) => {
    const itemId = item.product_id || item.inventory_id;
    if (form.items.find(i => i.item_id === itemId)) return;
    
    setForm({
      ...form,
      items: [...form.items, { 
        item_id: itemId, 
        item_name: item.product_name, 
        quantity: 1, 
        unit: item.unit,
        available_qty: item.quantity
      }]
    });
  };

  const handleAddCustomItem = () => {
    if (!customItemName.trim()) {
      toast({ title: "Error", description: "Please enter item name", variant: "destructive" });
      return;
    }
    
    const customId = `custom_${Date.now()}`;
    setForm({
      ...form,
      items: [...form.items, { 
        item_id: customId, 
        item_name: customItemName.trim(), 
        quantity: 1, 
        unit: customItemUnit,
        is_custom: true
      }]
    });
    setCustomItemName("");
  };

  const handleItemQuantityChange = (itemId, value) => {
    setForm({
      ...form,
      items: form.items.map(i => 
        i.item_id === itemId ? { ...i, quantity: parseFloat(value) || 0 } : i
      )
    });
  };

  const handleRemoveItem = (itemId) => {
    setForm({
      ...form,
      items: form.items.filter(i => i.item_id !== itemId)
    });
  };

  const handleSubmit = async () => {
    if (!form.outlet_id || form.items.length === 0 || !form.vehicle_number || !form.driver_name || !form.transport_medium) {
      toast({ title: "Error", description: "Please fill all required fields", variant: "destructive" });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops-extended/dispatches?ops_manager_id=${user.user_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, kitchen_id: locations.kitchen.kitchen_id })
      });
      
      if (res.ok) {
        const result = await res.json();
        if (result.warning) {
          toast({ 
            title: "Dispatch recorded with warnings", 
            description: result.warning,
          });
        } else {
          toast({ title: "Dispatch recorded successfully" });
        }
        setShowForm(false);
        setForm({
          outlet_id: "",
          items: [],
          vehicle_number: "",
          driver_name: "",
          num_persons: 1,
          transport_medium: "",
          notes: ""
        });
        fetchData();
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to record dispatch", variant: "destructive" });
    }
  };

  const getOutletName = (outletId) => {
    const outlet = locations.outlets?.find(o => o.outlet_id === outletId);
    return outlet?.name || outletId;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { color: "bg-blue-500", icon: Clock, label: "Confirmed" },
      processing: { color: "bg-yellow-500", icon: Package, label: "Processing" },
      dispatched: { color: "bg-purple-500", icon: Truck, label: "Dispatched" },
      delivered: { color: "bg-green-500", icon: CheckCircle, label: "Delivered" },
      cancelled: { color: "bg-red-500", icon: X, label: "Cancelled" }
    };
    const config = statusConfig[status] || statusConfig.confirmed;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  if (!locations.kitchen) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>You are not assigned to any kitchen.</p>
              <p className="text-sm mt-2">Please contact admin to be assigned to a kitchen.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6" />
            Dispatches
          </h1>
          <p className="text-muted-foreground">Kitchen: {locations.kitchen?.name}</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? <X className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          {showForm ? "Cancel" : "New Dispatch"}
        </Button>
      </div>

      {/* New Dispatch Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Dispatch</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Outlet *</Label>
                <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select outlet" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.outlets.map((outlet) => (
                      <SelectItem key={outlet.outlet_id} value={outlet.outlet_id}>{outlet.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Transport Medium *</Label>
                <Select value={form.transport_medium} onValueChange={(v) => setForm({ ...form, transport_medium: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select transport" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bike">Bike</SelectItem>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Vehicle Number *</Label>
                <Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} placeholder="e.g., KA-01-AB-1234" />
              </div>
              <div>
                <Label>Driver Name *</Label>
                <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} placeholder="Driver name" />
              </div>
              <div>
                <Label>Number of Persons</Label>
                <Input type="number" min={1} value={form.num_persons} onChange={(e) => setForm({ ...form, num_persons: parseInt(e.target.value) || 1 })} />
              </div>
            </div>

            {/* Items Selection from Inventory */}
            <div>
              <Label>Dispatch Items * (Select from inventory or add custom)</Label>
              
              {kitchenInventory.length > 0 && (
                <div className="mt-2 mb-3">
                  <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                    <Package className="h-4 w-4" /> Kitchen Inventory
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {kitchenInventory.map((item) => (
                      <Button
                        key={item.inventory_id}
                        variant={form.items.find(i => i.item_id === item.product_id) ? "secondary" : "outline"}
                        size="sm"
                        onClick={() => handleAddItem(item)}
                      >
                        {item.product_name}
                        <Badge 
                          variant={item.quantity < 10 ? "destructive" : "secondary"} 
                          className="ml-2 text-xs"
                        >
                          {item.quantity} {item.unit}
                        </Badge>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Custom Item Input */}
              <div className="flex gap-2 items-end mt-3 p-3 border rounded-lg bg-muted/50">
                <div className="flex-1">
                  <Label className="text-xs">Add Custom Item</Label>
                  <Input 
                    placeholder="Item name" 
                    value={customItemName}
                    onChange={(e) => setCustomItemName(e.target.value)}
                  />
                </div>
                <div className="w-24">
                  <Label className="text-xs">Unit</Label>
                  <Select value={customItemUnit} onValueChange={setCustomItemUnit}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pcs">pcs</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="liters">liters</SelectItem>
                      <SelectItem value="boxes">boxes</SelectItem>
                      <SelectItem value="packets">packets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" onClick={handleAddCustomItem}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Selected Items */}
              {form.items.length > 0 && (
                <div className="border rounded-lg p-3 space-y-2 mt-3">
                  {form.items.map((item) => (
                    <div key={item.item_id} className="flex items-center gap-3">
                      <span className="flex-1">
                        {item.item_name}
                        {item.is_custom && <Badge variant="outline" className="ml-2 text-xs">Custom</Badge>}
                      </span>
                      {item.available_qty !== undefined && item.quantity > item.available_qty && (
                        <AlertTriangle className="h-4 w-4 text-yellow-500" title="Exceeds available stock" />
                      )}
                      <Input 
                        type="number" 
                        min={0.1}
                        step={0.1}
                        value={item.quantity}
                        onChange={(e) => handleItemQuantityChange(item.item_id, e.target.value)}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground w-16">{item.unit}</span>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(item.item_id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
            </div>

            <Button onClick={handleSubmit} className="w-full">Record Dispatch</Button>
          </CardContent>
        </Card>
      )}

      {/* Pending Orders Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-yellow-500" />
            Pending Orders
            {pendingOrders.length > 0 && (
              <Badge variant="destructive">{pendingOrders.length} pending</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingOrders.map((order) => (
                <TableRow key={order.order_id}>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{order.order_id?.slice(-8)}</code>
                  </TableCell>
                  <TableCell>{order.user_name || "Customer"}</TableCell>
                  <TableCell>
                    <div className="text-sm max-w-[200px]">
                      {order.items?.map((item, idx) => (
                        <span key={idx} className="block">{item.name} × {item.quantity}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>₹{order.total_amount?.toFixed(2)}</TableCell>
                  <TableCell>{getStatusBadge(order.status)}</TableCell>
                  <TableCell className="text-sm">{new Date(order.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {pendingOrders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    No pending orders
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Completed Dispatches Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-green-500" />
            Dispatch History
            <Badge variant="outline">{dispatches.length} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dispatch #</TableHead>
                <TableHead>Outlet</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Transport</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Date/Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dispatches.map((dispatch) => (
                <TableRow key={dispatch.dispatch_id}>
                  <TableCell>
                    <Badge variant="outline">#{dispatch.dispatch_number}</Badge>
                  </TableCell>
                  <TableCell>{getOutletName(dispatch.outlet_id)}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {dispatch.items?.map((item, idx) => (
                        <span key={idx} className="block">{item.item_name} ({item.quantity} {item.unit})</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {dispatch.transport_medium}
                      <br />
                      <span className="text-muted-foreground">{dispatch.vehicle_number}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {dispatch.driver_name}
                      <br />
                      <span className="text-muted-foreground">{dispatch.num_persons} person(s)</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{dispatch.date}</div>
                    <span className="text-muted-foreground">
                      {new Date(dispatch.created_at).toLocaleTimeString()}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {dispatches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    No dispatches recorded yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
